import React, { useEffect, useRef, useState, useMemo } from 'react';
import { 
  ISO_A_SIZES, 
  ISO_B_SIZES,
  ISO_C_SIZES,
  US_SIZES,
  ANSI_SIZES,
  ARCH_SIZES,
  JIS_SIZES,
  US_ENVELOPE_SIZES,
  PHOTO_SIZES,
  COMMON_REFERENCE_OBJECTS 
} from '../data/standardSizes';

// Conversion constants
const INCH_TO_MM = 25.4;

// Helper function to get guide color based on item color
const getGuideColor = (itemColor) => {
  switch(itemColor) {
    case 'blue':
      return '#3b82f6'; // blue-500
    case 'green':
      return '#10b981'; // emerald-500
    case 'yellow':
      return '#f59e0b'; // amber-500
    case 'indigo':
      return '#7c3aed'; // violet-600
    case 'orange':
      return '#f97316'; // orange-500
    case 'purple':
      return '#d946ef'; // fuchsia-500
    case 'pink':
      return '#f43f5e'; // rose-500
    default:
      return '#6b7280'; // gray-500
  }
};

export const SizeVisualization = ({ 
  dimensions,
  selectedSizes, 
  selectedReferenceObjects,
  showGrid, 
  zoomLevel, 
  setZoomLevel,
  autoZoom, 
  autoZoomMode,
  displayUnits = 'mm', // Can be 'mm' or 'inches'
  aspectRatio = 1.414 // Default to ISO paper aspect ratio (√2:1)
}) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [items, setItems] = useState([]);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  
  // Define visualization constants
  const GRID_SIZE = displayUnits === 'mm' ? 10 : 1; // 10mm or 1 inch grid
  const PADDING = 20; // px
  
  // Helper function to decide which measurements to show to avoid overcrowding
  const visibleMeasurements = useMemo(() => {
    // Always show measurements when zoomed in enough
    if (scale > 0.5) {
      return items.map(item => true);
    }
    
    // Logic for which items to show when zoomed out
    // 1. Sort items by area (largest first)
    const itemsWithIndex = items.map((item, index) => ({ item, index }));
    itemsWithIndex.sort((a, b) => {
      const areaA = a.item.width * a.item.height;
      const areaB = b.item.width * b.item.height;
      return areaB - areaA; // Largest first
    });
    
    // 2. Initialize results with all false
    const results = new Array(items.length).fill(false);
    
    // 3. Take the largest 5 items and mark them as visible
    const topItems = Math.min(itemsWithIndex.length, 5);
    for (let i = 0; i < topItems; i++) {
      results[itemsWithIndex[i].index] = true;
    }
    
    return results;
  }, [items, scale]);
  
  // Helper function to find paper size by ID
  const findPaperSize = (standard, sizeId) => {
    // For debugging
    console.log(`Looking for size with standard: ${standard}, sizeId: ${sizeId}`);
    
    switch(standard) {
      case 'iso':
        const isoSize = ISO_A_SIZES.find(size => size.id === sizeId);
        if (isoSize) return isoSize;
        break;
      case 'isob':
        const isoBSize = ISO_B_SIZES.find(size => size.id === sizeId);
        if (isoBSize) return isoBSize;
        break;
      case 'isoc':
        const isoCSize = ISO_C_SIZES.find(size => size.id === sizeId);
        if (isoCSize) return isoCSize;
        break;
      case 'us':
        const usSize = US_SIZES.find(size => size.id === sizeId);
        if (usSize) return usSize;
        break;
      case 'ansi':
        const ansiSize = ANSI_SIZES.find(size => size.id === sizeId);
        if (ansiSize) return ansiSize;
        break;
      case 'arch':
        const archSize = ARCH_SIZES.find(size => size.id === sizeId);
        if (archSize) return archSize;
        break;
      case 'jis':
        const jisSize = JIS_SIZES.find(size => size.id === sizeId);
        if (jisSize) return jisSize;
        break;
      case 'usenv':
        const usEnvSize = US_ENVELOPE_SIZES.find(size => size.id === sizeId);
        if (usEnvSize) return usEnvSize;
        break;
      case 'photo':
        const photoSize = PHOTO_SIZES.find(size => size.id === sizeId);
        if (photoSize) return photoSize;
        break;
    }
    
    console.warn(`No size found for standard: ${standard}, sizeId: ${sizeId}`);
    return null;
  };
  
  // Helper function to find reference object by ID
  const findReferenceObject = (id) => {
    return COMMON_REFERENCE_OBJECTS.find(obj => obj.id === id);
  };
  
  // Helper function to convert dimensions to display units
  const convertToDisplayUnits = (value, standard) => {
    // If incoming value is in inches (all our data) and display is mm
    if (displayUnits === 'mm') {
      return value * INCH_TO_MM;
    }
    // If display is inches, return as is (all our data is stored in inches)
    return value;
  };
  
  // Format value for display based on current units
  const formatForDisplay = (value) => {
    if (displayUnits === 'mm') {
      return `${Math.round(value)} mm`;
    }
    return `${value.toFixed(2)}"`;
  };
  
  // Parse selected sizes and reference objects
  useEffect(() => {
    const newItems = [];
    
    // Add selected paper sizes
    Object.entries(selectedSizes).forEach(([id, isSelected]) => {
      if (isSelected) {
        // Get paper size data from the corresponding data array
        const [standard, ...sizeIdParts] = id.split('-');
        const sizeId = sizeIdParts.join('-'); // Handle IDs that might contain hyphens
        
        console.log(`Processing: standard=${standard}, sizeId=${sizeId}, full id=${id}`);
        
        const paperSize = findPaperSize(standard, sizeId);
        
        if (paperSize) {
          // Convert dimensions to display units
          const width = convertToDisplayUnits(paperSize.width, paperSize.standard);
          const height = convertToDisplayUnits(paperSize.height, paperSize.standard);
          
          // Determine color based on standard
          let color;
          if (standard.startsWith('iso')) {
            color = 'blue';
          } else if (standard === 'us' || standard === 'usenv') {
            color = 'yellow';
          } else if (standard === 'ansi') {
            color = 'indigo';
          } else if (standard === 'arch') {
            color = 'orange';
          } else if (standard === 'jis') {
            color = 'purple';
          } else if (standard === 'photo') {
            color = 'pink';
          } else {
            color = 'gray';
          }
          
          newItems.push({
            id: id,
            type: 'paperSize',
            name: paperSize.name,
            width: width,
            height: height,
            widthRaw: paperSize.width,
            heightRaw: paperSize.height,
            color: color,
            standard: paperSize.standard
          });
        } else {
          console.warn(`Could not find paper size for: ${id} (standard: ${standard}, sizeId: ${sizeId})`);
        }
      }
    });
    
    // Add reference objects
    selectedReferenceObjects.forEach(id => {
      // Get reference object data
      const refObject = findReferenceObject(id);
      
      if (refObject) {
        // Convert dimensions to display units
        const width = convertToDisplayUnits(refObject.width);
        const height = convertToDisplayUnits(refObject.height);
        
        newItems.push({
          id: id,
          type: 'referenceObject',
          name: refObject.name,
          width: width,
          height: height,
          widthRaw: refObject.width,
          heightRaw: refObject.height,
          color: 'gray'
        });
      }
    });
    
    // Add custom dimensions if valid
    if (dimensions.width.mm && dimensions.height.mm &&
        !isNaN(parseFloat(dimensions.width.mm)) && !isNaN(parseFloat(dimensions.height.mm))) {
      // Convert custom dimensions based on display units
      let width, height;
      
      if (displayUnits === 'mm') {
        width = parseFloat(dimensions.width.mm);
        height = parseFloat(dimensions.height.mm);
      } else {
        width = parseFloat(dimensions.width.inches);
        height = parseFloat(dimensions.height.inches);
      }
      
      newItems.push({
        id: 'custom',
        type: 'customSize',
        name: 'Your Size',
        width: width,
        height: height,
        widthRaw: parseFloat(dimensions.width.inches),
        heightRaw: parseFloat(dimensions.height.inches),
        color: 'green'
      });
    }
    
    setItems(newItems);
  }, [selectedSizes, selectedReferenceObjects, dimensions, displayUnits]);
  
  // Handle auto-zoom when triggered
  useEffect(() => {
    if (!autoZoom || !containerRef.current || items.length === 0 || autoZoomMode === 'none') return;
    
    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width - (PADDING * 2);
    const containerHeight = containerRect.height - (PADDING * 2);
    
    // Determine which items to zoom to
    let itemsToZoom = [];
    if (autoZoomMode === 'last') {
      // Find the most recently selected/added item
      const lastSelected = [...items].reverse().find(item => item.type !== 'customSize') || items[0];
      itemsToZoom = [lastSelected];
    } else {
      // Include all items
      itemsToZoom = [...items];
    }
    
    if (itemsToZoom.length === 0) return;
    
    console.log(`Auto-zooming to ${itemsToZoom.length} items`);
    console.log('Items to zoom:', itemsToZoom);
    
    // Find the bounding box of all items to zoom
    let minX = Number.MAX_VALUE;
    let minY = Number.MAX_VALUE;
    let maxX = Number.MIN_VALUE;
    let maxY = Number.MIN_VALUE;
    
    // Find the extreme dimensions across all items to zoom
    itemsToZoom.forEach(item => {
      minX = Math.min(minX, 0);
      minY = Math.min(minY, 0);
      maxX = Math.max(maxX, item.width);
      maxY = Math.max(maxY, item.height);
    });
    
    console.log(`Bounding box: (${minX},${minY}) to (${maxX},${maxY})`);
    
    // Add padding
    minX -= 40;
    minY -= 40;
    maxX += 40;
    maxY += 40;
    
    // Calculate the zoom level to fit all items
    const boundingWidth = maxX - minX;
    const boundingHeight = maxY - minY;
    
    const scaleX = containerWidth / boundingWidth;
    const scaleY = containerHeight / boundingHeight;
    const newScale = Math.min(scaleX, scaleY) * 0.9; // 90% of max scale for margin
    
    // Calculate the new pan position to center the items
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;
    
    const newPanX = (containerWidth / 2) - (centerX * newScale);
    const newPanY = (containerHeight / 2) - (centerY * newScale);
    
    console.log(`New scale: ${newScale}, new pan: (${newPanX}, ${newPanY})`);
    
    // Update the state
    setScale(newScale);
    setPan({ x: newPanX, y: newPanY });
    setZoomLevel(newScale);
    
  }, [autoZoom, autoZoomMode, items, setZoomLevel]);
  
  // Update canvas size when container or zoom changes
  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({
          width: rect.width,
          height: rect.height
        });
      }
    };
    
    updateSize();
    
    // Apply zoom level
    setScale(zoomLevel);
    
    // Listen for resize events
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [zoomLevel]);
  
  // Mouse event handlers for panning
  const handleMouseDown = (e) => {
    // Left mouse button for panning (no modifiers required)
    if (e.button === 0) {
      setIsDragging(true);
      setStartPan({
        x: e.clientX - pan.x,
        y: e.clientY - pan.y
      });
      e.preventDefault();
    }
  };
  
  const handleMouseMove = (e) => {
    if (isDragging) {
      setPan({
        x: e.clientX - startPan.x,
        y: e.clientY - startPan.y
      });
      e.preventDefault();
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Wheel event handler for zooming - no modifiers needed
  const handleWheel = (e) => {
    e.preventDefault(); // Prevent page scrolling
    
    // Get the mouse position relative to the canvas
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate the point in world space before zooming
    const worldX = (mouseX - pan.x) / scale;
    const worldY = (mouseY - pan.y) / scale;
    
    // Calculate the new scale
    const delta = -e.deltaY;
    const zoomFactor = delta > 0 ? 1.1 : 0.9;
    const newScale = Math.max(0.1, Math.min(10, scale * zoomFactor));
    
    // Calculate the new pan position to keep the point under the mouse
    const newPanX = mouseX - (worldX * newScale);
    const newPanY = mouseY - (worldY * newScale);
    
    // Update the state
    setScale(newScale);
    setPan({ x: newPanX, y: newPanY });
    setZoomLevel(newScale);
  };
  
  // Render the visualization
  return (
    <div 
      ref={containerRef} 
      className="size-visualization-container"
      style={{ 
        width: '100%', 
        height: '100%', 
        position: 'relative', 
        overflow: 'hidden',
        backgroundColor: '#f9fafb',
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      <canvas 
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}
        style={{ position: 'absolute' }}
      />
      
      {/* Grid */}
      {showGrid && (
        <div 
          className="grid"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'linear-gradient(#ddd 1px, transparent 1px), linear-gradient(90deg, #ddd 1px, transparent 1px)',
            backgroundSize: `${GRID_SIZE * scale}px ${GRID_SIZE * scale}px`,
            transform: `translate(${pan.x % (GRID_SIZE * scale)}px, ${pan.y % (GRID_SIZE * scale)}px)`,
            pointerEvents: 'none'
          }}
        />
      )}
      
      {/* Items */}
      <div className="debug-info" style={{ position: 'absolute', top: '5px', left: '5px', zIndex: 1000, fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.7)', padding: '5px', pointerEvents: 'none' }}>
        Items: {items.length}, Scale: {scale.toFixed(2)}, Pan: ({pan.x.toFixed(0)}, {pan.y.toFixed(0)})
      </div>
      
      {items.map((item, index) => {
        // Get the appropriate color values based on the item's color
        let borderColor, bgColor;
        switch(item.color) {
          case 'blue':
            borderColor = '#3b82f6'; // blue-500
            bgColor = 'rgba(59, 130, 246, 0.1)';
            break;
          case 'green':
            borderColor = '#10b981'; // emerald-500
            bgColor = 'rgba(16, 185, 129, 0.1)';
            break;
          case 'yellow':
            borderColor = '#f59e0b'; // amber-500
            bgColor = 'rgba(245, 158, 11, 0.1)';
            break;
          case 'indigo':
            borderColor = '#7c3aed'; // violet-600
            bgColor = 'rgba(124, 58, 237, 0.1)';
            break;
          case 'orange':
            borderColor = '#f97316'; // orange-500
            bgColor = 'rgba(249, 115, 22, 0.1)';
            break;
          case 'purple':
            borderColor = '#d946ef'; // fuchsia-500
            bgColor = 'rgba(217, 70, 239, 0.1)';
            break;
          case 'pink':
            borderColor = '#f43f5e'; // rose-500
            bgColor = 'rgba(244, 63, 94, 0.1)';
            break;
          default:
            borderColor = '#6b7280'; // gray-500
            bgColor = 'rgba(107, 114, 128, 0.1)';
        }
        
        return (
          <div
            key={item.id}
            className="visualization-item"
            style={{
              position: 'absolute',
              left: `${pan.x}px`,
              top: `${pan.y}px`,
              width: `${item.width * scale}px`,
              height: `${item.height * scale}px`,
              border: `2px solid ${borderColor}`,
              backgroundColor: bgColor,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pointerEvents: 'none'
            }}
          >
          <div 
            className="item-label"
            style={{
              backgroundColor: 'white',
              padding: '2px 6px',
              borderRadius: '2px',
              fontSize: `${Math.max(10, Math.min(12 * scale, 16))}px`, // Cap font size
              whiteSpace: 'nowrap',
              fontWeight: '500',
              color: '#4b5563'
            }}
          >
            {item.name}
          </div>
        </div>
        );
      })}
      
      {/* Measurement guides */}
      {items.map((item, index) => {
        // Use the visibility logic from our useMemo hook
        const shouldShowMeasurement = visibleMeasurements[index];
        
        return (
          <React.Fragment key={`guide-${item.id}`}>
            {/* Width guide */}
            <div
              className="measurement-guide width"
              style={{
                position: 'absolute',
                left: `${pan.x}px`,
                top: `${pan.y + (item.height * scale) + 8}px`,
                width: `${item.width * scale}px`,
                height: '1px',
                backgroundColor: getGuideColor(item.color),
                pointerEvents: 'none',
                opacity: shouldShowMeasurement ? 1 : 0.3
              }}
            />
            {shouldShowMeasurement && (
              <div
                className="measurement-label width"
                style={{
                  position: 'absolute',
                  left: `${pan.x + (item.width * scale / 2) - 20}px`,
                  top: `${pan.y + (item.height * scale) + 10}px`,
                  backgroundColor: 'white',
                  padding: '0 4px',
                  borderRadius: '2px',
                  fontSize: `${Math.max(8, Math.min(10 * scale, 14))}px`, // Cap font size between 8 and 14 pixels
                  color: getGuideColor(item.color),
                  pointerEvents: 'none'
                }}
              >
                {formatForDisplay(item.width)}
              </div>
            )}
            
            {/* Height guide */}
            <div
              className="measurement-guide height"
              style={{
                position: 'absolute',
                left: `${pan.x + (item.width * scale) + 8}px`,
                top: `${pan.y}px`,
                width: '1px',
                height: `${item.height * scale}px`,
                backgroundColor: getGuideColor(item.color),
                pointerEvents: 'none',
                opacity: shouldShowMeasurement ? 1 : 0.3
              }}
            />
            {shouldShowMeasurement && (
              <div
                className="measurement-label height"
                style={{
                  position: 'absolute',
                  left: `${pan.x + (item.width * scale) + 10}px`,
                  top: `${pan.y + (item.height * scale / 2) - 10}px`,
                  backgroundColor: 'white',
                  padding: '0 4px',
                  borderRadius: '2px',
                  fontSize: `${Math.max(8, Math.min(10 * scale, 14))}px`, // Cap font size between 8 and 14 pixels
                  color: getGuideColor(item.color),
                  pointerEvents: 'none'
                }}
              >
                {formatForDisplay(item.height)}
              </div>
            )}
          </React.Fragment>
        );
      })}
      
      {/* Info overlay */}
      <div 
        className="info-overlay"
        style={{
          position: 'absolute',
          bottom: '10px',
          left: '10px',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#6b7280',
          pointerEvents: 'none'
        }}
      >
        <div>Drag: Pan</div>
        <div>Wheel: Zoom</div>
      </div>
    </div>
  );
};